import type { StrapiCustomerRow, StrapiQuotationGroupRow } from '../../../api/strapiCrm';
import type { StrapiSampleInformationRow } from '../../../api/strapiCrm';
import type { QuotationRecurringTypeValue } from './quotationFormConstants';
import { quotationDatetimeLocalFromIso } from './quotationFormDateTime';
import {
  QUOTATION_TRIWULAN_RECURRING_INTERVAL,
  quotationFormGroupTotalRecurringCount,
  recurringIntervalLabel,
  type QuotationContractHeaderInput,
} from './quotationRecurring';
import { quotationFormSampleLineSubtotal } from './quotationFormSampleMoney';
import type { QuotationFormOtherCostRow, QuotationFormSampleRow, QuotationFormValues } from './quotationFormTypes';
import { quotationFormSamplesFromDetailLines, validateQuotationFormSamplesForSave } from './TabScreens/Samples';
import {
  quotationFormOtherCostsFromDetailLines,
  quotationFormOtherCostsSubtotalFromRows,
  validateQuotationFormOtherCostsForSave,
} from './TabScreens/BiayaLainnya';
import type { StrapiQuotationOtherCostRow } from '../../../api/strapiCrm';

export type QuotationFormGroupRow = {
  key: string;
  documentId?: string;
  customer: StrapiCustomerRow | null;
  recurring_type: QuotationRecurringTypeValue | '';
  /** Interval recurring (default 1). */
  recurring_interval: string;
  /** Override jumlah event; kosong = hitung otomatis dari periode kontrak. */
  recurring_count_override: string;
  /** Tanggal mulai recurring group; kosong = pakai contract_period_start_date header. */
  recurring_start_date: string;
  sampleRows: QuotationFormSampleRow[];
  otherCostRows: QuotationFormOtherCostRow[];
};

export function quotationUsesSampleGroups(
  values: Pick<QuotationFormValues, 'is_contract' | 'is_consultant_quote'>,
): boolean {
  return values.is_contract || values.is_consultant_quote;
}

export function emptyQuotationFormGroupRow(): QuotationFormGroupRow {
  return {
    key: `grp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    customer: null,
    recurring_type: '',
    recurring_interval: '1',
    recurring_count_override: '',
    recurring_start_date: '',
    sampleRows: [],
    otherCostRows: [],
  };
}

function clearQuotationGroupContractFields(group: QuotationFormGroupRow): QuotationFormGroupRow {
  return {
    ...group,
    recurring_type: '',
    recurring_interval: '1',
    recurring_count_override: '',
    recurring_start_date: '',
  };
}

function clearQuotationGroupConsultantFields(group: QuotationFormGroupRow): QuotationFormGroupRow {
  return {
    ...group,
    customer: null,
  };
}

/** Ubah flag kontrak/konsultan: hide di UI + reset field terkait yang tidak dipakai lagi. */
export function patchQuotationContractFlags(
  values: QuotationFormValues,
  patch: Partial<Pick<QuotationFormValues, 'is_contract' | 'is_consultant_quote'>>,
): Partial<QuotationFormValues> {
  const nextContract = patch.is_contract ?? values.is_contract;
  const nextConsultant = patch.is_consultant_quote ?? values.is_consultant_quote;
  const uncheckingContract = patch.is_contract === false && values.is_contract;
  const uncheckingConsultant = patch.is_consultant_quote === false && values.is_consultant_quote;
  const willUseGroups = nextContract || nextConsultant;

  if (!willUseGroups) {
    const flatRows =
      values.sampleGroups.length > 0
        ? values.sampleGroups.flatMap((g) => g.sampleRows)
        : values.sampleRows;
    const flatCosts =
      values.sampleGroups.length > 0
        ? values.sampleGroups.flatMap((g) => g.otherCostRows)
        : values.otherCostRows;
    return {
      is_contract: false,
      is_consultant_quote: false,
      contract_period_start_date: '',
      contract_period_end_date: '',
      sampleRows: flatRows,
      otherCostRows: flatCosts,
      sampleGroups: [],
    };
  }

  const wasUsingGroups = values.is_contract || values.is_consultant_quote;
  let sampleGroups = values.sampleGroups;
  if (!wasUsingGroups && sampleGroups.length === 0) {
    const wrapped = emptyQuotationFormGroupRow();
    wrapped.sampleRows = values.sampleRows;
    wrapped.otherCostRows = values.otherCostRows;
    sampleGroups = [wrapped];
  }
  if (uncheckingContract && nextConsultant) {
    sampleGroups = sampleGroups.map(clearQuotationGroupContractFields);
  }
  if (uncheckingConsultant && nextContract) {
    sampleGroups = sampleGroups.map(clearQuotationGroupConsultantFields);
  }

  const result: Partial<QuotationFormValues> = {
    is_contract: nextContract,
    is_consultant_quote: nextConsultant,
    sampleGroups,
    ...(wasUsingGroups
      ? {}
      : {
          sampleRows: [],
          otherCostRows: [],
        }),
  };

  if (uncheckingContract) {
    result.contract_period_start_date = '';
    result.contract_period_end_date = '';
  }

  return result;
}

export function allQuotationFormSampleRows(values: QuotationFormValues): QuotationFormSampleRow[] {
  if (quotationUsesSampleGroups(values)) {
    return values.sampleGroups.flatMap((g) => g.sampleRows);
  }
  return values.sampleRows;
}

function customerStubFromGroupRow(c: NonNullable<StrapiQuotationGroupRow['customer']>): StrapiCustomerRow {
  return {
    documentId: c.documentId,
    customer_number: c.customer_number,
    name: c.name,
  };
}

export function quotationFormAllOtherCostRows(values: QuotationFormValues): QuotationFormOtherCostRow[] {
  if (quotationUsesSampleGroups(values)) {
    return values.sampleGroups.flatMap((g) => g.otherCostRows);
  }
  return values.otherCostRows;
}

function quotationFormOtherCostsForGroup(
  groupDocumentId: string | undefined,
  allCosts: StrapiQuotationOtherCostRow[],
  opts: { isFirstGroup: boolean },
): QuotationFormOtherCostRow[] {
  const groupId = groupDocumentId?.trim();
  if (groupId) {
    return quotationFormOtherCostsFromDetailLines(
      allCosts.filter((c) => c.quotation_group?.documentId === groupId),
    );
  }
  if (opts.isFirstGroup) {
    return quotationFormOtherCostsFromDetailLines(
      allCosts.filter((c) => !c.quotation_group?.documentId?.trim()),
    );
  }
  return [];
}

function formGroupFromPersisted(
  group: StrapiQuotationGroupRow,
  groupLines: StrapiSampleInformationRow[],
  allOtherCosts: StrapiQuotationOtherCostRow[],
  groupIndex: number,
): QuotationFormGroupRow {
  return {
    key: group.documentId,
    documentId: group.documentId,
    customer: group.customer ? customerStubFromGroupRow(group.customer) : null,
    recurring_type: (group.recurring_type ?? '') as QuotationRecurringTypeValue | '',
    recurring_interval:
      group.recurring_interval != null && Number.isFinite(Number(group.recurring_interval))
        ? String(Math.max(1, Number(group.recurring_interval)))
        : '1',
    recurring_count_override:
      group.recurring_count_override != null && Number.isFinite(Number(group.recurring_count_override))
        ? String(Math.max(1, Number(group.recurring_count_override)))
        : '',
    recurring_start_date: group.recurring_start_date
      ? quotationDatetimeLocalFromIso(group.recurring_start_date)
      : '',
    sampleRows: quotationFormSamplesFromDetailLines(groupLines),
    otherCostRows: quotationFormOtherCostsForGroup(group.documentId, allOtherCosts, {
      isFirstGroup: groupIndex === 0,
    }),
  };
}

/** Rebuild grouped form state from sample lines and/or persisted quotation_groups. */
export function quotationFormGroupsFromDetailLines(
  lines: StrapiSampleInformationRow[],
  allOtherCosts: StrapiQuotationOtherCostRow[] = [],
  persistedGroups: StrapiQuotationGroupRow[] = [],
): QuotationFormGroupRow[] {
  const byGroup = new Map<string, { group: StrapiQuotationGroupRow; lines: StrapiSampleInformationRow[] }>();
  const order: string[] = [];

  const ensureGroup = (group: StrapiQuotationGroupRow) => {
    const id = group.documentId?.trim();
    if (!id) return;
    const existing = byGroup.get(id);
    if (!existing) {
      order.push(id);
      byGroup.set(id, { group, lines: [] });
      return;
    }
    if (!existing.group.customer?.documentId && group.customer?.documentId) {
      existing.group = group;
    }
  };

  for (const group of persistedGroups) {
    ensureGroup(group);
  }

  const ungrouped: StrapiSampleInformationRow[] = [];
  for (const line of lines) {
    const g = line.quotation_group;
    if (!g?.documentId) {
      ungrouped.push(line);
      continue;
    }
    ensureGroup(g);
    byGroup.get(g.documentId)!.lines.push(line);
  }

  const grouped = order.map((id, groupIndex) => {
    const { group, lines: groupLines } = byGroup.get(id)!;
    return formGroupFromPersisted(group, groupLines, allOtherCosts, groupIndex);
  });

  if (ungrouped.length) {
    const wrapped = emptyQuotationFormGroupRow();
    wrapped.key = 'ungrouped-samples';
    wrapped.sampleRows = quotationFormSamplesFromDetailLines(ungrouped);
    wrapped.otherCostRows = quotationFormOtherCostsForGroup(undefined, allOtherCosts, {
      isFirstGroup: grouped.length === 0,
    });
    grouped.push(wrapped);
  }

  return grouped;
}

export function quotationFormFlatSamplesFromDetailLines(
  lines: StrapiSampleInformationRow[],
): QuotationFormSampleRow[] {
  const flat = lines.filter((l) => !l.quotation_group?.documentId);
  if (flat.length) return quotationFormSamplesFromDetailLines(flat);
  // Quotation flat: baris tanpa quotation_group; fallback jika data lama masih punya relasi group.
  return lines.length ? quotationFormSamplesFromDetailLines(lines) : [];
}

export type QuotationFormGroupSampleSubtotal = {
  groupIndex: number;
  groupKey: string;
  recurringCount: number;
  subtotal: number;
};

function groupRecurringCountForFinancial(
  group: QuotationFormGroupRow,
  header: QuotationContractHeaderInput,
  is_contract: boolean,
): number {
  return is_contract ? (quotationFormGroupTotalRecurringCount(group, header) ?? 1) : 1;
}

export function quotationFormGroupSampleSubtotal(
  group: QuotationFormGroupRow,
  header: QuotationContractHeaderInput,
  is_contract: boolean,
): { recurringCount: number; subtotal: number } {
  const recurringCount = groupRecurringCountForFinancial(group, header, is_contract);
  let subtotal = 0;
  for (const row of group.sampleRows) {
    subtotal += quotationFormSampleLineSubtotal(row, recurringCount);
  }
  return { recurringCount, subtotal };
}

export type QuotationFormGroupsSaveValidatedGroup = {
  kind: 'create';
  payload: {
    customer?: string;
    recurring_type?: Exclude<QuotationRecurringTypeValue, 'triwulan'>;
    recurring_interval?: number;
    recurring_count_override?: number | null;
    recurring_start_date?: string | null;
    sample_lines: Array<{
      jenis_pengukuran: string;
      parameter_uji: string;
      baku_mutu: string;
      sample_point_count: number;
      harga: number | null;
    }>;
    /** Baris sampel yang sudah ada (mis. konversi quotation flat → group). */
    sample_updates?: Array<{
      documentId: string;
      payload: {
        jenis_pengukuran: string;
        parameter_uji: string;
        baku_mutu: string;
        sample_point_count: number;
        harga: number | null;
      };
    }>;
    other_costs: Array<{
      description: string;
      quantity: number;
      cost_price: number;
    }>;
    other_cost_updates?: Array<{
      documentId: string;
      payload: {
        description: string;
        quantity: number;
        cost_price: number;
      };
    }>;
  };
};

export type QuotationFormGroupsPersistedUpdateGroup = {
  kind: 'update';
  documentId: string;
  payload: {
    customer?: string | null;
    recurring_type?: Exclude<QuotationRecurringTypeValue, 'triwulan'> | null;
    recurring_interval?: number | null;
    recurring_count_override?: number | null;
    recurring_start_date?: string | null;
    sample_creates: QuotationFormGroupsSaveValidatedGroup['payload']['sample_lines'];
    sample_updates: Array<{
      documentId: string;
      payload: {
        jenis_pengukuran: string;
        parameter_uji: string;
        baku_mutu: string;
        sample_point_count: number;
        harga: number | null;
      };
    }>;
    other_cost_creates: QuotationFormGroupsSaveValidatedGroup['payload']['other_costs'];
    other_cost_updates: Array<{
      documentId: string;
      payload: {
        description: string;
        quantity: number;
        cost_price: number;
      };
    }>;
  };
};

function parseGroupRecurringInterval(raw: string): number | null {
  const t = raw.trim();
  if (!t) return 1;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

function parseGroupRecurringCountOverride(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number.parseInt(t, 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

function parseGroupRecurringStartIso(raw: string): string | null {
  const t = raw.trim();
  if (!t) return null;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function contractGroupScalars(group: QuotationFormGroupRow): {
  recurring_interval: number;
  recurring_count_override: number | null;
  recurring_start_date: string | null;
} {
  return {
    recurring_interval:
      group.recurring_type === 'triwulan'
        ? QUOTATION_TRIWULAN_RECURRING_INTERVAL
        : parseGroupRecurringInterval(group.recurring_interval) ?? 1,
    recurring_count_override: parseGroupRecurringCountOverride(group.recurring_count_override),
    recurring_start_date: parseGroupRecurringStartIso(group.recurring_start_date),
  };
}

export function validateQuotationFormGroupsForSave(
  groups: QuotationFormGroupRow[],
  opts: { is_contract: boolean; is_consultant_quote: boolean },
):
  | { ok: false; message: string }
  | {
      ok: true;
      creates: QuotationFormGroupsSaveValidatedGroup[];
      updates: QuotationFormGroupsPersistedUpdateGroup[];
    } {
  if (groups.length === 0) {
    return { ok: false, message: 'Tambah minimal satu group sampel (tombol Tambah Sample di tab Detail).' };
  }

  const creates: QuotationFormGroupsSaveValidatedGroup[] = [];
  const updates: QuotationFormGroupsPersistedUpdateGroup[] = [];

  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi];
    const label = gi + 1;
    if (opts.is_consultant_quote && !group.customer?.documentId?.trim()) {
      return { ok: false, message: `Sampel (${label}): pelanggan group wajib dipilih untuk penawaran konsultan.` };
    }
    const rawRecurringType = group.recurring_type?.trim() ?? '';
    const submitRecurringType = rawRecurringType === 'triwulan' ? 'monthly' : rawRecurringType;
    if (opts.is_contract) {
      if (!['weekly', 'monthly', 'yearly'].includes(submitRecurringType)) {
        return {
          ok: false,
          message: `Sampel (${label}): jenis perulangan wajib dipilih untuk penawaran kontrak.`,
        };
      }
      const ri = parseGroupRecurringInterval(group.recurring_interval);
      if (group.recurring_interval.trim() && ri == null) {
        return {
          ok: false,
          message: `Sampel (${label}): interval perulangan harus bilangan bulat minimal 1.`,
        };
      }
      const rco = parseGroupRecurringCountOverride(group.recurring_count_override);
      if (group.recurring_count_override.trim() && rco == null) {
        return {
          ok: false,
          message: `Sampel (${label}): penyesuaian jumlah perulangan harus bilangan bulat minimal 1 bila diisi.`,
        };
      }
      if (group.recurring_start_date.trim() && !parseGroupRecurringStartIso(group.recurring_start_date)) {
        return {
          ok: false,
          message: `Sampel (${label}): tanggal mulai perulangan tidak valid.`,
        };
      }
    }
    const samplesBundle = validateQuotationFormSamplesForSave(group.sampleRows);
    if (!samplesBundle.ok) {
      return { ok: false, message: `Sampel (${label}): ${samplesBundle.message}` };
    }
    const otherCostsBundle = validateQuotationFormOtherCostsForSave(group.otherCostRows);
    if (!otherCostsBundle.ok) {
      return { ok: false, message: `Sampel (${label}): ${otherCostsBundle.message}` };
    }

    const contractScalars = opts.is_contract ? contractGroupScalars(group) : null;
    if (group.documentId) {
      updates.push({
        kind: 'update',
        documentId: group.documentId,
        payload: {
          customer: opts.is_consultant_quote ? group.customer?.documentId ?? null : null,
          recurring_type: opts.is_contract
            ? (submitRecurringType as 'weekly' | 'monthly' | 'yearly')
            : null,
          ...(contractScalars ?? {}),
          sample_creates: samplesBundle.creates.map((c) => c.payload),
          sample_updates: samplesBundle.updates.map((u) => ({
            documentId: u.documentId,
            payload: u.payload,
          })),
          other_cost_creates: otherCostsBundle.creates.map((c) => c.payload),
          other_cost_updates: otherCostsBundle.updates.map((u) => ({
            documentId: u.documentId,
            payload: u.payload,
          })),
        },
      });
    } else {
      creates.push({
        kind: 'create',
        payload: {
          ...(opts.is_consultant_quote && group.customer?.documentId
            ? { customer: group.customer.documentId }
            : {}),
          ...(opts.is_contract && submitRecurringType
            ? {
                recurring_type: submitRecurringType as 'weekly' | 'monthly' | 'yearly',
                ...contractScalars!,
              }
            : {}),
          sample_lines: samplesBundle.creates.map((c) => c.payload),
          sample_updates: samplesBundle.updates.map((u) => ({
            documentId: u.documentId,
            payload: u.payload,
          })),
          other_costs: otherCostsBundle.creates.map((c) => c.payload),
          other_cost_updates: otherCostsBundle.updates.map((u) => ({
            documentId: u.documentId,
            payload: u.payload,
          })),
        },
      });
    }
  }

  return { ok: true, creates, updates };
}

export function quotationFormContractHeaderFromValues(
  values: Pick<
    QuotationFormValues,
    'contract_period_start_date' | 'contract_period_end_date'
  >,
): QuotationContractHeaderInput {
  return {
    contract_period_start_date: values.contract_period_start_date,
    contract_period_end_date: values.contract_period_end_date,
  };
}

function capitalizeFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Label section group untuk preview/PDF (konsultan: customer; kontrak: frekuensi × jadwal). */
export function quotationFormGroupSectionLabel(
  group: QuotationFormGroupRow,
  opts: {
    groupIndex: number;
    is_contract: boolean;
    is_consultant_quote: boolean;
    contractHeader: QuotationContractHeaderInput;
  },
): string {
  const groupNum = opts.groupIndex + 1;
  if (opts.is_consultant_quote) {
    return group.customer?.name?.trim() || `Group ${groupNum}`;
  }
  if (opts.is_contract) {
    const rt = group.recurring_type === 'triwulan' ? 'monthly' : group.recurring_type;
    const intervalParsed = Number.parseInt(group.recurring_interval.trim() || '1', 10);
    const interval = Number.isFinite(intervalParsed) && intervalParsed >= 1 ? intervalParsed : 1;
    const freqLabel =
      rt === 'weekly' || rt === 'monthly' || rt === 'yearly'
        ? capitalizeFirst(recurringIntervalLabel(rt, interval))
        : '—';
    const count = quotationFormGroupTotalRecurringCount(group, opts.contractHeader) ?? 1;
    return `Group ${groupNum} — ${freqLabel} · ${count}×`;
  }
  return `Group ${groupNum}`;
}

export type QuotationPreviewSampleSection = {
  sectionLabel: string;
  sampleRows: QuotationFormSampleRow[];
  recurringCount: number;
  otherCostRows: QuotationFormOtherCostRow[];
};

/** Section sampel untuk preview dokumen (grouped kontrak/konsultan atau flat). */
export function quotationPreviewSampleSectionsFromRow(
  values: Pick<
    QuotationFormValues,
    | 'is_contract'
    | 'is_consultant_quote'
    | 'contract_period_start_date'
    | 'contract_period_end_date'
  > & {
    sampleGroups: QuotationFormGroupRow[];
    sampleRows: QuotationFormSampleRow[];
    otherCostRows?: QuotationFormOtherCostRow[];
  },
): { usesGroups: boolean; sections: QuotationPreviewSampleSection[] } {
  if (!quotationUsesSampleGroups(values) || !values.sampleGroups.length) {
    const breakdown = quotationFormFinancialBreakdown({
      ...values,
      otherCostRows: values.otherCostRows ?? [],
    });
    return {
      usesGroups: false,
      sections: [
        {
          sectionLabel: '',
          sampleRows: breakdown.rows,
          recurringCount: 1,
          otherCostRows: [],
        },
      ],
    };
  }

  const contractHeader = quotationFormContractHeaderFromValues(values);
  const sections: QuotationPreviewSampleSection[] = values.sampleGroups.map((group, groupIndex) => ({
    sectionLabel: quotationFormGroupSectionLabel(group, {
      groupIndex,
      is_contract: values.is_contract,
      is_consultant_quote: values.is_consultant_quote,
      contractHeader,
    }),
    sampleRows: group.sampleRows,
    recurringCount: values.is_contract
      ? (quotationFormGroupTotalRecurringCount(group, contractHeader) ?? 1)
      : 1,
    otherCostRows: group.otherCostRows,
  }));

  return { usesGroups: true, sections };
}

export type QuotationFormGroupOtherCostSubtotal = {
  groupIndex: number;
  groupKey: string;
  recurringCount: number;
  subtotal: number;
};

export type QuotationFormFinancialBreakdown = {
  usesGroups: boolean;
  rows: QuotationFormSampleRow[];
  recurringCounts: number[];
  groupSubtotals: QuotationFormGroupSampleSubtotal[];
  allOtherCostRows: QuotationFormOtherCostRow[];
  otherCostRecurringCounts: number[];
  groupOtherCostSubtotals: QuotationFormGroupOtherCostSubtotal[];
};

/** Baris sampel + multiplier recurring per baris; total = Σ per group (Σ per sample × jadwal group). */
export function quotationFormFinancialBreakdown(
  values: Pick<
    QuotationFormValues,
    | 'is_contract'
    | 'is_consultant_quote'
    | 'contract_period_start_date'
    | 'contract_period_end_date'
    | 'sampleGroups'
    | 'sampleRows'
  > & {
    otherCostRows?: QuotationFormOtherCostRow[];
  },
): QuotationFormFinancialBreakdown {
  const flatOtherCostRows = values.otherCostRows ?? [];
  if (quotationUsesSampleGroups(values)) {
    const header = quotationFormContractHeaderFromValues(values);
    const rows: QuotationFormSampleRow[] = [];
    const recurringCounts: number[] = [];
    const otherCostRecurringCounts: number[] = [];
    const groupSubtotals: QuotationFormGroupSampleSubtotal[] = [];
    const groupOtherCostSubtotals: QuotationFormGroupOtherCostSubtotal[] = [];

    values.sampleGroups.forEach((group, groupIndex) => {
      const { recurringCount, subtotal } = quotationFormGroupSampleSubtotal(
        group,
        header,
        values.is_contract,
      );
      const otherCostMultiplier = values.is_contract ? recurringCount : 1;
      groupSubtotals.push({
        groupIndex,
        groupKey: group.key,
        recurringCount,
        subtotal,
      });
      groupOtherCostSubtotals.push({
        groupIndex,
        groupKey: group.key,
        recurringCount: otherCostMultiplier,
        subtotal: quotationFormOtherCostsSubtotalFromRows(group.otherCostRows, otherCostMultiplier),
      });
      for (const row of group.sampleRows) {
        rows.push(row);
        recurringCounts.push(recurringCount);
      }
      for (let i = 0; i < group.otherCostRows.length; i += 1) {
        otherCostRecurringCounts.push(otherCostMultiplier);
      }
    });

    return {
      usesGroups: true,
      rows,
      recurringCounts,
      groupSubtotals,
      allOtherCostRows: values.sampleGroups.flatMap((g) => g.otherCostRows),
      otherCostRecurringCounts,
      groupOtherCostSubtotals,
    };
  }

  return {
    usesGroups: false,
    rows: values.sampleRows,
    recurringCounts: values.sampleRows.map(() => 1),
    groupSubtotals: [],
    allOtherCostRows: flatOtherCostRows,
    otherCostRecurringCounts: flatOtherCostRows.map(() => 1),
    groupOtherCostSubtotals: [],
  };
}

/** @deprecated Prefer `quotationFormFinancialBreakdown`. */
export function quotationFormFinancialSamplesWithRecurringCounts(
  values: Pick<
    QuotationFormValues,
    | 'is_contract'
    | 'is_consultant_quote'
    | 'contract_period_start_date'
    | 'contract_period_end_date'
    | 'sampleGroups'
    | 'sampleRows'
  >,
): { rows: QuotationFormSampleRow[]; recurringCounts: number[] } {
  const breakdown = quotationFormFinancialBreakdown({ ...values, otherCostRows: [] });
  return { rows: breakdown.rows, recurringCounts: breakdown.recurringCounts };
}

export function quotationGroupApiDataFromSavePayload(
  payload: QuotationFormGroupsSaveValidatedGroup['payload'],
): {
  customer?: { connect: string[] };
  recurring_type?: 'weekly' | 'monthly' | 'yearly';
  recurring_interval?: number;
  recurring_count_override?: number | null;
  recurring_start_date?: string | null;
} {
  const out: ReturnType<typeof quotationGroupApiDataFromSavePayload> = {};
  if (payload.customer) out.customer = { connect: [payload.customer] };
  if (payload.recurring_type) {
    out.recurring_type = payload.recurring_type;
    out.recurring_interval = payload.recurring_interval ?? 1;
    out.recurring_count_override = payload.recurring_count_override ?? null;
    out.recurring_start_date = payload.recurring_start_date ?? null;
  }
  return out;
}
